import { Stack } from '@cmg/design-system';
import { Form, FormikProvider, useFormik, yupToFormErrors } from 'formik';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { useIndicationActivityBuySideContext } from '../../../../context/IndicationActivityBuySideContext';
import { IndicationSectionWithCreateButtons } from '../../common/indication-section-create-view/IndicationSectionWithCreateButtons';
import IndicationSectionForm from '../../common/indication-section-form/IndicationSectionForm';
import { PassIndicationDialog } from '../../common/PassIndicationDialog';
import {
  CommonIndicationValidationSchemaContext,
  IndicationFormValues,
  validationSchema,
} from '../../common/utils/IndicationSectionValidation';
import { usePassOnOffering } from '../common/hooks/usePassOnOffering';
import { useCreateMyIndication } from './hooks/useCreateMyIndication';
import { createInitialValues } from './IndicationSectionCreateView.model';

const EMPTY_ARRAY = [];
const IndicationSectionCreateView: React.FC = () => {
  const { basicPublishedOffering, offeringId, loading, orderBookConfiguration, publishedOffering } =
    useIndicationActivityBuySideContext();
  const offering = basicPublishedOffering!;

  const [createMyIndication] = useCreateMyIndication(offeringId);

  const [isPassDialogOpen, setIsPassDialogOpen] = React.useState(false);

  const formik = useFormik<IndicationFormValues>({
    initialValues: createInitialValues(offering),
    validate: async values => {
      try {
        await validationSchema.validate(values, {
          abortEarly: false,
          context: {
            offeringType: offering.type,
            interestLevels: values.interestLevels,
            isLimitIndicationsOnlyEnabled: orderBookConfiguration.isLimitIndicationsOnlyEnabled,
            isIndicationPriceAsPercentageParEnabled:
              orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
            isOnlySharesUnitOnIndicationsEnabled:
              orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled,
          } satisfies CommonIndicationValidationSchemaContext,
        });

        return {};
      } catch (e) {
        return yupToFormErrors(e);
      }
    },
    onSubmit: createMyIndication,
  });
  const { isSubmitting, handleSubmit, handleReset } = formik;
  const isLoading = loading || isSubmitting;

  const [onPass, isPassing] = usePassOnOffering({
    offeringId,
    onClose: () => setIsPassDialogOpen(false),
  });

  return (
    <IndicationSectionWithCreateButtons
      isLoading={isLoading}
      handlePass={() => setIsPassDialogOpen(true)}
      handleReset={handleReset}
      handleSubmit={handleSubmit}
    >
      <Stack gap={1}>
        <FormikProvider value={formik}>
          <Form>
            <IndicationSectionForm
              filings={defaultTo(publishedOffering?.terms.filings, null)}
              securityType={defaultTo(publishedOffering?.security.type, null)}
              tranches={defaultTo(offering.orderEntryProfile?.tranches, EMPTY_ARRAY)}
              instruments={defaultTo(publishedOffering?.instruments, null)}
              issuerName={defaultTo(publishedOffering?.issuer.name, null)}
              offeringType={offering.type}
              pricingCurrencyCode={offering.pricingCurrencyCode}
              securityTypeDisplayName={offering.securityTypeDisplayName!}
              isLimitIndicationsOnlyEnabled={orderBookConfiguration.isLimitIndicationsOnlyEnabled}
              isOnlySharesUnitOnIndicationsEnabled={
                orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled
              }
              isIndicationPriceAsPercentageParEnabled={
                orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
              }
            />
          </Form>
        </FormikProvider>

        <PassIndicationDialog
          isOpen={isPassDialogOpen}
          isLoading={isPassing}
          onClose={() => setIsPassDialogOpen(false)}
          onPass={onPass}
        />
      </Stack>
    </IndicationSectionWithCreateButtons>
  );
};

export default IndicationSectionCreateView;
